import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Check,
    AlertTriangle,
    MinusCircle,
    Loader2,
    Lock,
    ShieldCheck,
    FileSignature,
    ArrowRight,
    Wallet,
    Ban,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { OutcomeBadge } from "@/components/OutcomeBadge";
import { ScenarioTabs } from "@/components/ScenarioTabs";
import { getRequest, validateRequest, signRequest } from "@/lib/api";

const REPLAY = [
    { id: "TX-BOND-001", label: "Bond - approved" },
    { id: "TX-BOND-002", label: "Bond - freeze" },
    { id: "TX-FUND-001", label: "Fund unit - replay" },
];

const CHECK_ICON = {
    Pass: { icon: Check, cls: "text-green-600" },
    "Repair required": { icon: AlertTriangle, cls: "text-amber-600" },
    "Not invoked": { icon: MinusCircle, cls: "text-slate-300" },
};

const STEPS = [
    ["REQUEST", "Seller Custodian Role Wallet requests a transfer."],
    ["BOUNDARY", "The request carries only the minimum structured data and evidence references."],
    ["AUTHORITY", "Transfer Agent Role Wallet evaluates policy-bound authority."],
    ["GOVERNANCE", "KATLAS creates and verifies the decision receipt."],
    ["EXECUTION", "The adapter may act only if the decision is ALLOW."],
    ["HANDOFF", "Registrar Role Wallet receives the permitted lifecycle event."],
];

const GOVERNANCE_BOUNDARY =
    "Governance boundary: evidence retained by role wallets; only references and authorised decision metadata enter the shared control flow.";

const STEP_DETAIL = {
    1: "Evidence custody: retained by originating and accountable role wallets. Shared: policy reference, authority context, evidence-reference status and required structured transaction data.",
    3: "Shared proof: KATLAS attests to the governed handoff; it does not store the underlying private evidence.",
    4: "The execution adapter may act only when it receives an authorised ALLOW decision.",
};

export default function Workbench() {
    const { eventId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [stage, setStage] = useState("idle"); // idle | validating | validated | signing | signed
    const [validation, setValidation] = useState(null);
    const [signResult, setSignResult] = useState(null);

    const load = () => {
        setError(null);
        getRequest(eventId)
            .then(setData)
            .catch((e) => setError(e?.response?.status === 404 ? "unknown" : "load_failed"));
    };

    useEffect(() => {
        setData(null);
        setError(null);
        setStage("idle");
        setValidation(null);
        setSignResult(null);
        getRequest(eventId)
            .then(setData)
            .catch((e) => setError(e?.response?.status === 404 ? "unknown" : "load_failed"));
    }, [eventId]);

    const runValidation = async () => {
        setStage("validating");
        const res = await validateRequest(eventId);
        setTimeout(() => {
            setValidation(res);
            setStage("validated");
        }, 650);
    };

    const runSign = async () => {
        setStage("signing");
        const res = await signRequest(eventId);
        setSignResult(res);
        setStage("signed");
    };

    if (error)
        return (
            <Layout>
                <div data-testid="scenario-error">
                    <p className="text-sm text-slate-600">
                        {error === "unknown"
                            ? "Unknown synthetic request."
                            : "Scenario could not be loaded. Please return to the workbench or retry."}
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                        {error !== "unknown" && (
                            <button
                                onClick={load}
                                data-testid="scenario-retry-btn"
                                className="rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900"
                            >
                                Retry
                            </button>
                        )}
                        <Link to="/" className="text-sm text-blue-950 underline">
                            Return to overview
                        </Link>
                    </div>
                </div>
            </Layout>
        );

    if (!data)
        return (
            <Layout>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading envelope…
                </div>
            </Layout>
        );

    const { envelope, decision, role_wallets, protocol } = data;
    const checks = validation?.checks || data.checks;
    const isFreeze = decision.outcome !== "ALLOW";

    // active step index for the handoff strip
    let activeStep = 0;
    if (stage === "validating") activeStep = 2;
    else if (stage === "validated") activeStep = 3;
    else if (stage === "signing") activeStep = 3;
    else if (stage === "signed") activeStep = isFreeze ? 3 : 5;

    return (
        <Layout wide>
            <ScenarioTabs active="transfer" />

            {/* Replay selector */}
            <div className="mb-6 flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-mono uppercase tracking-[0.14em] text-slate-400">
                    Same control, different asset
                </span>
                {REPLAY.map((r) => (
                    <Link
                        key={r.id}
                        to={`/request/${r.id}`}
                        data-testid={`replay-${r.id}`}
                        className={`rounded-sm border px-3 py-1 text-xs transition-colors duration-150 ${
                            r.id === eventId
                                ? "border-blue-950 bg-blue-950 text-white"
                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                        {r.label}
                    </Link>
                ))}
            </div>

            {/* Institutional role wallets - governed handoff */}
            <section className="mb-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="role-wallets">
                <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-blue-950" strokeWidth={1.5} />
                    <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                        Institutional role wallets · governed handoff
                    </p>
                </div>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                    Each actor is a governed identity and bounded-authority container - not
                    a consumer crypto wallet. Custody, authority and receipt role differ by
                    institution.
                </p>
                <div className="mt-5 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 lg:grid-cols-5">
                    {role_wallets.map((w, i) => (
                        <div
                            key={w.institution}
                            data-testid={`wallet-${i}`}
                            className={`bg-white p-4 ${w.blocked ? "opacity-60" : ""}`}
                        >
                            <p className="font-mono text-[10px] uppercase tracking-wide text-slate-400">
                                {w.role}
                            </p>
                            <p className="mt-1 text-sm font-medium leading-tight text-slate-900">
                                {w.institution}
                            </p>
                            <dl className="mt-3 space-y-2 text-xs">
                                <WalletField label="Custody" value={w.custody} />
                                <WalletField label="Authority" value={w.authority} />
                                <WalletField label="Protocol role" value={w.protocol_role} />
                            </dl>
                            {w.blocked && (
                                <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-amber-700">
                                    <Ban className="h-3 w-3" /> no authorised action
                                </p>
                            )}
                            <p className="mt-3 text-[11px] text-slate-400">↓ {w.step}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-3 text-[11px] italic text-slate-400" data-testid="role-wallet-honesty">
                    Synthetic role-wallet model. Demonstrates the KATLAS governance
                    pattern; not a live market-infrastructure deployment.
                </p>
            </section>

            {/* Governed interaction context - role-to-role protocol */}
            <section className="mb-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="protocol-context">
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                    Governed interaction context
                </p>
                <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                    <ProtocolRow label="Protocol" value={protocol.protocol} mono />
                    <ProtocolRow label="Evidence exchange" value={protocol.evidence_exchange} />
                    <ProtocolRow label="Initiating role" value={protocol.initiating_role} />
                    <ProtocolRow label="Decision role" value={protocol.decision_role} />
                    <ProtocolRow label="Receiving role" value={protocol.receiving_role} />
                    <ProtocolRow label="Shared state" value={protocol.shared_state} />
                </dl>
                <p className="mt-4 max-w-3xl text-xs leading-relaxed text-slate-500">
                    The protocol defines which role may perform each step, what minimum
                    information may cross the handoff, what evidence remains local, and
                    what downstream system may act on the result.
                </p>
            </section>
            <section className="mb-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="handoff-sequence">
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                    Controlled handoff
                </p>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                    The action begins in an Institutional Role Wallet, travels through a
                    purpose-bound role-to-role protocol, and reaches downstream execution
                    only after a governed decision is attested.
                </p>
                <div className="mt-4 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-6">
                    {STEPS.map(([k, v], i) => {
                        const done = stage === "signed" && (!isFreeze || i <= 3);
                        const active = i === activeStep && stage !== "idle";
                        const blocked = isFreeze && i >= 4 && stage === "signed";
                        return (
                            <div
                                key={k}
                                data-testid={`step-${i}`}
                                className={`bg-white p-4 ${blocked ? "opacity-50" : ""} ${
                                    active ? "ring-1 ring-inset ring-blue-950" : ""
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-[10px] text-slate-300">{String(i + 1).padStart(2, "0")}</span>
                                    {done && !blocked && <Check className="h-3.5 w-3.5 text-green-600" />}
                                    {blocked && <Ban className="h-3.5 w-3.5 text-amber-600" />}
                                </div>
                                <p className="mt-1 font-mono text-xs uppercase tracking-wide text-slate-900">{k}</p>
                                <p className="mt-1 text-[11px] leading-snug text-slate-500">{v}</p>
                                {STEP_DETAIL[i] && (
                                    <p className="mt-2 border-t border-slate-100 pt-2 text-[10px] leading-snug text-slate-400">
                                        {STEP_DETAIL[i]}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
                <p className="mt-4 max-w-3xl text-xs leading-relaxed text-slate-500" data-testid="downstream-adapters-note">
                    The same governed decision can be consumed by different execution
                    adapters: token transfer, lock, release, event notification, register
                    update or ISO 20022-oriented workflow.
                </p>
                {isFreeze && stage === "signed" && (                    <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-800" data-testid="freeze-hardstop">
                        <p className="font-medium">Hard stop between governance and execution</p>
                        <p className="mt-1">KATLAS decision: FREEZE · Execution adapter: no authorised action received · Registrar: no register update invoked.</p>
                        <div className="mt-3 border-t border-amber-200 pt-2 font-mono text-[11px] text-amber-700">
                            <p>Protocol state: paused for accountable review</p>
                            <p>Execution adapter: no authorised instruction received</p>
                            <p>Registrar Role Wallet: no register-update event received</p>
                        </div>
                    </div>
                )}
            </section>

            <div className="grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 lg:grid-cols-[1fr_1fr]">
                {/* LEFT - Request summary / envelope */}
                <div className="bg-white p-7">
                    <div className="flex items-center justify-between">
                        <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                            Control Decision Envelope
                        </p>
                        <span className="font-mono text-xs text-slate-400">{envelope.event_id}</span>
                    </div>
                    <h1 className="mt-3 text-xl font-medium leading-tight tracking-tight text-slate-900">
                        {envelope.requested_action} · {envelope.asset_class}
                    </h1>
                    <p className="mt-1 text-sm text-slate-600">{envelope.instrument_reference}</p>

                    <dl className="mt-6 divide-y divide-slate-100 border-y border-slate-100">
                        <Field label="Acting institution" value={`${envelope.acting_institution} · ${envelope.acting_role}`} />
                        <Field label="Counterparty" value={`${envelope.counterparty_institution} · ${envelope.counterparty_role}`} />
                        <Field label="Transfer agent" value={envelope.transfer_agent} />
                        <Field label="Registrar" value={envelope.registrar} />
                        <Field label="Quantity" value={envelope.quantity} mono />
                        <Field label="Policy reference" value={`${envelope.policy_id} / v${envelope.policy_version}`} mono />
                        <Field label="Eligibility" value={envelope.eligibility_status} />
                        <Field label="Restriction / jurisdiction" value={envelope.restriction_status} />
                        <Field label="Custody reference" value={envelope.custody_reference_synthetic} mono />
                        <Field label="Settlement reference" value={envelope.settlement_reference} mono />
                        <Field label="Structured-data quality" value={envelope.structured_data_quality} />
                    </dl>

                    <div className="mt-6">
                        <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                            Evidence references (shared with KATLAS)
                        </p>
                        <ul className="mt-2 space-y-1">
                            {envelope.shared_evidence_references.map((e) => (
                                <li key={e.hash} className="flex items-center justify-between gap-3 text-xs">
                                    <span className="text-slate-600">{e.label}</span>
                                    <span className="font-mono text-slate-400">{e.hash}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-2 text-xs text-slate-400">
                            <Lock className="mr-1 inline h-3 w-3" />
                            {envelope.withheld_evidence_count} evidence reference(s) withheld under custody.
                        </p>
                    </div>

                    <p className="mt-6 rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">
                        {GOVERNANCE_BOUNDARY}
                    </p>
                </div>

                {/* RIGHT - Controls / decision / receipt */}
                <div className="bg-white p-7">
                    <div className="flex items-center justify-between">
                        <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                            Governed validation
                        </p>
                        {stage === "idle" && (
                            <button
                                onClick={runValidation}
                                data-testid="run-validation-btn"
                                className="inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                            >
                                <ShieldCheck className="h-4 w-4" /> Run governed validation
                            </button>
                        )}
                        {stage === "validating" && (
                            <span className="inline-flex items-center gap-2 text-sm text-slate-400">
                                <Loader2 className="h-4 w-4 animate-spin" /> Evaluating…
                            </span>
                        )}
                    </div>

                    {stage === "idle" && (
                        <p className="mt-4 text-sm text-slate-500">
                            Run the control function to evaluate authority, eligibility,
                            restriction, evidence and settlement-context conditions. The
                            token adapter cannot act until this governed decision is signed.
                        </p>
                    )}

                    {(stage === "validated" || stage === "signing" || stage === "signed") && (
                        <div className="mt-4 fade-up" data-testid="control-checks">
                            <ul className="divide-y divide-slate-100 border-y border-slate-100">
                                {checks.map((c, i) => {
                                    const m = CHECK_ICON[c.result] || CHECK_ICON["Not invoked"];
                                    return (
                                        <motion.li
                                            key={c.name}
                                            initial={{ opacity: 0, x: 6 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.05 * i }}
                                            className="flex items-center justify-between py-2.5"
                                            data-testid={`check-${i}`}
                                        >
                                            <span className="text-sm text-slate-700">{c.name}</span>
                                            <span className={`inline-flex items-center gap-1.5 text-xs ${m.cls}`}>
                                                <m.icon className="h-4 w-4" strokeWidth={1.8} />
                                                {c.result}
                                            </span>
                                        </motion.li>
                                    );
                                })}
                            </ul>

                            {/* Decision */}
                            <div className="mt-5 rounded-sm border border-slate-200 bg-slate-50 p-4" data-testid="authority-decision">
                                <div className="flex items-center gap-3">
                                    <OutcomeBadge outcome={decision.outcome} />
                                    <span className="text-sm font-medium text-slate-900">{decision.headline}</span>
                                </div>
                                {decision.narrative && (
                                    <p className="mt-2 text-xs leading-relaxed text-slate-600">{decision.narrative}</p>
                                )}
                                <div className="mt-3 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
                                    <KV label="Reason code" value={decision.reason_code} />
                                    <KV label="Acting role" value={decision.acting_role} />
                                    <KV label="Policy reference" value={decision.policy_reference} />
                                    <KV label="Enforcement" value={decision.enforcement_instruction} />
                                </div>
                            </div>

                            {/* Step 2: governed receipt */}
                            {stage === "validated" && (
                                <button
                                    onClick={runSign}
                                    data-testid="sign-receipt-btn"
                                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-blue-950 px-4 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                                >
                                    <FileSignature className="h-4 w-4" />
                                    Sign governance receipt through KATLAS node
                                </button>
                            )}
                            {stage === "signing" && (
                                <div className="mt-5 inline-flex items-center gap-2 text-sm text-slate-400">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Contacting live KATLAS node…
                                </div>
                            )}

                            {stage === "signed" && signResult && (
                                <SignOutcome result={signResult} decision={decision} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

function SignOutcome({ result, decision }) {
    const proofNode = result.signed
        ? "active"
        : result.katlas_state === "unconfigured"
        ? "honestly unconfigured"
        : result.katlas_state;

    return (
        <div className="mt-5 fade-up">
            {/* KATLAS governance proof */}
            <div className="rounded-sm border border-slate-300 bg-white p-4" data-testid="governance-proof">
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                    KATLAS governance proof
                </p>
                <div className="mt-3 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
                    <KV label="Node" value={proofNode} />
                    <KV label="Authority decision" value={decision.outcome} />
                    <KV label="Evidence" value="References only; source evidence retained" />
                    <KV
                        label="Receipt"
                        value={result.signed ? "Signed by KATLAS node" : "Not produced (no fabrication)"}
                    />
                    <KV
                        label="Verification"
                        value={result.signed ? result.public?.verification_status || "verified" : "unavailable"}
                    />
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
                    The KATLAS node receives the minimum governance payload needed to
                    attest to the authorised handoff. Private evidence, credentials and
                    source records remain under the custody of the accountable
                    Institutional Role Wallets.
                </p>
            </div>

            {!result.signed ? (
                <div className="mt-4 rounded-sm border border-slate-300 bg-slate-50 p-4" data-testid="sign-unconfigured">
                    <div className="flex items-center gap-2">
                        <Lock className="h-4 w-4 text-slate-500" />
                        <p className="text-sm font-medium text-slate-800">
                            {result.katlas_label || "KATLAS unavailable"}
                        </p>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-slate-600">{result.message}</p>
                    <p className="mt-2 text-xs leading-relaxed text-slate-500">
                        The governance pattern is visible. Live KATLAS attestation
                        activates only when the backend-only production credential is
                        provisioned. No KATLAS-signed receipt is produced, fabricated or
                        simulated in this state.
                    </p>
                </div>
            ) : (
                <SignSuccess result={result} />
            )}
        </div>
    );
}

function SignSuccess({ result }) {
    const pub = result.public;
    return (
        <div data-testid="sign-success">
            <div className="mt-4 rounded-sm border border-slate-200 p-4">
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                    Synthetic execution adapter
                </p>
                <ul className="mt-2 space-y-1">
                    {result.execution.lines.map((l) => (
                        <li
                            key={l}
                            className={`flex items-center gap-2 text-sm ${
                                result.execution.invoked ? "text-green-700" : "text-amber-700"
                            }`}
                        >
                            {result.execution.invoked ? <Check className="h-4 w-4" /> : <MinusCircle className="h-4 w-4" />}
                            {l}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="mt-4 rounded-sm border border-slate-200 bg-slate-50 p-4">
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                    KATLAS receipt - verified by node
                </p>
                <div className="mt-2 grid grid-cols-1 gap-1 text-xs">
                    <KV label="Receipt hash" value={pub.receipt_hash} mono />
                    <KV label="Signer / attestation" value={pub.signer_attestation_reference} mono />
                    <KV label="Node timestamp" value={pub.node_timestamp} mono />
                    <KV label="Verification status" value={pub.verification_status} />
                </div>
                <Link
                    to={`/r/${pub.receipt_hash}`}
                    data-testid="open-receipt-link"
                    className="mt-4 inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-900 transition-colors duration-150 hover:bg-slate-50"
                >
                    Open public-safe receipt
                    <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </div>
    );
}

function WalletField({ label, value }) {
    return (
        <div>
            <dt className="font-mono text-[10px] uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className="mt-0.5 leading-snug text-slate-700">{value}</dd>
        </div>
    );
}

function ProtocolRow({ label, value, mono }) {
    return (
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-1.5">
            <dt className="text-xs font-mono uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className={`text-right text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value}</dd>
        </div>
    );
}

function Field({ label, value, mono }) {
    return (
        <div className="flex items-start justify-between gap-4 py-2.5">
            <dt className="text-xs font-mono uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className={`text-right text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value}</dd>
        </div>
    );
}

function KV({ label, value, mono }) {
    const text = value == null ? "-"
        : typeof value === "object" ? Object.values(value).filter(Boolean).join(" · ")
        : value;
    return (
        <div className="flex flex-col">
            <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
            <span className={`text-slate-800 ${mono ? "font-mono break-all" : ""}`}>{text}</span>
        </div>
    );
}
